// apps/app/src/hooks/useRoadmap.ts
//
// Reads the live roadmap for the /admin/roadmap Roadmap tab (franklycatering #564).
//
// The SPA never talks to GitHub directly — it is a client-rendered Vite app, so a
// GitHub token in the browser would be a token in the bundle. The `roadmap-fetch`
// edge function holds ROADMAP_GITHUB_TOKEN server-side, gates on requireStaff, and
// returns only derived phase data.
//
// Types come from the edge function's own pure module rather than being re-declared
// here, so the client and the function cannot disagree about the payload shape. The
// `import type` is erased at compile time (no verbatimModuleSyntax), so nothing from
// supabase/functions/ reaches the browser bundle.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import type {
  RoadmapFetchResult,
  RoadmapItem,
  RoadmapItemStatus,
  RoadmapPhase,
} from "../../supabase/functions/roadmap-fetch/roadmap-derive.ts";

export type { RoadmapFetchResult, RoadmapItem, RoadmapItemStatus, RoadmapPhase };

const ROADMAP_QUERY_KEY = ["roadmap", "phases"] as const;

async function fetchRoadmap(): Promise<RoadmapFetchResult> {
  const { data, error } = await supabase.functions.invoke("roadmap-fetch", { body: {} });

  // Transport / HTTP failures (including the 401/403 requireStaff returns) arrive here.
  // The function's own reason is on error.context, not error.message (#719).
  if (error) {
    throw new Error(await edgeFunctionErrorMessage(error, error.message || "Couldn't load the roadmap"));
  }

  // A 200 carrying `{ phases: [], error: "..." }` is DATA, not a failure: the function
  // deliberately answers "no token configured" and "GitHub unreachable" that way so the
  // human-readable copy survives instead of being flattened into a transport error.
  // Only a payload that isn't the expected shape at all is a real fault.
  const payload = data as RoadmapFetchResult | null;
  if (!payload || typeof payload !== "object" || !Array.isArray(payload.phases)) {
    throw new Error("Roadmap response was not in the expected format.");
  }

  return payload;
}

export function useRoadmap() {
  return useQuery({
    queryKey: ROADMAP_QUERY_KEY,
    queryFn: fetchRoadmap,
    // Mirrors the 5-minute cache the marketing site gets from Next's `revalidate: 300`.
    // The edge function holds no cache of its own, so this is the only one — see #564 §1.
    staleTime: 5 * 60_000,
  });
}
